import { useState, useEffect } from 'react';
import { LocalizationProvider, DatePicker, DateCalendar, ClearIcon, CalendarIcon } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18next';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ro';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

const localeMap = {
  'en-GB': 'en-gb',
  'de-DE': 'de',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'ro-RO': 'ro',
  'pl-PL': 'pl',
};

type Language = keyof typeof localeMap;

const setDayjsLocale = (language: Language): string => {
  const dayjsLocale = localeMap[language] ?? 'en-gb';
  dayjs.locale(dayjsLocale);
  return dayjsLocale;
};

const setDayjsLocaleFromLocalStorage = (): string => {
  const storedLanguage = (localStorage.getItem('i18nextLng') ?? 'en-GB') as Language;
  const dayjsLocale = localeMap[storedLanguage] ?? 'en-gb';
  dayjs.locale(dayjsLocale);
  return dayjsLocale;
};

interface DatePickerLocalizedProps {
  label: string;
  value?: string | null;
  valueForDateCalendar?: Dayjs | null;
  disabled?: boolean,
  disableHighlightToday?: boolean,
  format?: string;
  useCalendar: boolean
  onChange?: (date: string | null) => void;
  onChangeDateCalendar?: (date: Dayjs | null) => void;
  shouldDisableDateCalendar?: (date: Dayjs) => boolean;
  shouldDisableDate?: (date: string) => boolean;
  translations?: {
    clearButtonLabel: string | undefined;
    todayButtonLabel: string | undefined;
    cancelButtonLabel: string | undefined;
    okButtonLabel: string | undefined;
    datePickerToolbarTitle?: string | undefined;
  };
  handleEnterKey?: (event: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent<HTMLButtonElement>) => void;
  minDate?: Dayjs;
    maxDate?: Dayjs;
    sx?: object;
}

const DatePickerLocalized: React.FC<DatePickerLocalizedProps> = ({
  label,
  value,
  valueForDateCalendar,
  disabled,
  disableHighlightToday,
  format = 'MM/DD/YYYY',
  useCalendar,
  onChange,
  shouldDisableDate,
  onChangeDateCalendar,
  shouldDisableDateCalendar,
  translations,
  handleEnterKey,
  minDate,
  maxDate,
  sx,
}) => {
  const [locale, setLocale] = useState<string>(setDayjsLocaleFromLocalStorage());
  const [openCalendar, setOpenCalendar] = useState(false);

  const { t } = useTranslation('common');

  useEffect(() => {
    const currentLanguage = i18n.language as Language;
    setDayjsLocale(currentLanguage);
    setLocale(localeMap[currentLanguage] ?? 'en-gb');
    const handleLanguageChange = (lng: Language) => {
      const newLocale = setDayjsLocale(lng);
      setLocale(newLocale);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const handleDateChange = (newValue: string | null) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleDateChangeDateCalendar = (newValue: Dayjs | null) => {
    if (onChangeDateCalendar) {
      onChangeDateCalendar(newValue);
    }
  };

  const handleClear = () => {
    handleDateChange('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}
      adapterLocale={locale}
      localeText={
        !useCalendar
          ? {
            clearButtonLabel: translations?.clearButtonLabel ?? t('calendar.clearButtonLabel'),
            todayButtonLabel: translations?.todayButtonLabel ?? t('calendar.todayButtonLabel'),
            cancelButtonLabel: translations?.cancelButtonLabel ?? t('calendar.cancelButtonLabel'),
            okButtonLabel: translations?.okButtonLabel ?? t('calendar.okButtonLabel'),
            datePickerToolbarTitle: translations?.datePickerToolbarTitle ?? t('calendar.title'),
          }
          : undefined
      }
    >
      {useCalendar ? (
        <DateCalendar
          value={valueForDateCalendar || (value ? dayjs(value) : null)}
          onChange={(newValue) => {
            handleDateChangeDateCalendar(newValue);
          }}
          sx={{
            maxWidth: '100%',
            '& .Mui-selected': {
              borderRadius: '4px',
              width: '40px',
              height: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            '& .MuiPickersDay-root': {
              fontWeight: 'bold',
            },
            '& .Mui-disabled': {
              fontWeight: 'normal',
              color: 'lightgrey',
            },
            '& .MuiYearCalendar-root': {
              width: 'auto',
            },
          }}
          shouldDisableDate={shouldDisableDateCalendar
            ? (date) => shouldDisableDateCalendar(dayjs(date))
            : undefined}
          disabled={disabled}
          disableHighlightToday={disableHighlightToday}
          showDaysOutsideCurrentMonth
          minDate={minDate}
          maxDate={maxDate}
        />
      ) : (
        <DatePicker
          minDate={minDate}
          maxDate={maxDate}
          label={label}
          value={valueForDateCalendar || (value ? dayjs(value, 'YYYY-MM-DD') : null)}
          onChange={(newValue) => {
            if (onChangeDateCalendar) {
              handleDateChangeDateCalendar(newValue);
            } else {
              const formattedDate = newValue ? dayjs(newValue).format('YYYY-MM-DD') : '';
              handleDateChange(formattedDate);
            }
          }}
          shouldDisableDate={(date) => {
            const dayjsDate = dayjs(date);
            const dateString = dayjsDate.format('YYYY-MM-DD');
            return shouldDisableDate ? shouldDisableDate(dateString) : false;
          }}
          format={format}
          disabled={disabled}
          sx={{ width: '100%', ...sx }} 
          open={openCalendar}
          onOpen={() => setOpenCalendar(true)}
          onClose={() => setOpenCalendar(false)}
          slotProps={{
            textField: {
              error: false,
              onKeyDown: (e) => { handleEnterKey && handleEnterKey(e); },
              InputProps: {
                readOnly: true,
                endAdornment: !disabled ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, marginRight: -4 }}>
                    {(valueForDateCalendar || value) && <IconButton sx={{ paddingRight: '5px', height: '20px', width: '20px', mr: 2 }}
                      onClick={() => {
                        if (onChangeDateCalendar) {
                          handleDateChangeDateCalendar(null);
                        } else {
                          handleClear();
                        }
                      }}
                      size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>}
                    <IconButton sx={{ paddingLeft: '5px', height: '20px', width: '20px' }}
                      size="small"
                      onClick={() => setOpenCalendar(true)}
                      onKeyDown={(e) => {
                        handleEnterKey && handleEnterKey(e);
                        handleEnterKey && e.preventDefault();
                      }}
                    >
                      <CalendarIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : undefined,
              },
            },
            field: {
              clearable: false,
              onClear: () => handleClear,
            },
            openPickerButton: {
              onKeyDown: (e) => {
                handleEnterKey && handleEnterKey(e);
                handleEnterKey && e.preventDefault();
              },
            },
          }}
        />
      )}
    </LocalizationProvider >
  );
};

export default DatePickerLocalized;
